<template>
  <div class="pricing-input">
    <message-box class="is-primary" title="Wskazówka" icon="fa-lightbulb">
      Pakiety mogą być wizualnie wyróżnione za pomocą opcji
      <icon name="fa-star" class="has-text-primary" />. Wówczas karta
      reprezentująca pakiet będzie delikatnie wysunięta w stosunku do innych.
    </message-box>
    <loader :state="state">
      <transition-group name="fade" class="pricing-table" tag="div">
        <div
          class="pricing-plan"
          v-for="(plan, planIndex) in table"
          :key="plan[Plan.ID]"
        >
          <editor
            placeholder="Nazwa..."
            :ref="`name:${planIndex}`"
            @blur="removePlanIfNameIsEmpty(planIndex)"
            class="plan-header"
            v-model.trim="plan[Plan.NAME]"
            :readonly="!hasPermission(Permission.CREATE_TEMPLATE)"
          />
          <div
            class="plan-price"
            v-for="(price, priceIndex) in plan[Plan.PRICES]"
            :key="priceIndex"
          >
            <editable
              placeholder="XX"
              class="plan-price-amount"
              v-model.trim="plan[Plan.PRICES][priceIndex].price"
              :ref="`price:${planIndex}:${priceIndex}`"
              :readonly="!hasPermission(Permission.CREATE_TEMPLATE)"
              @blur="removePriceIfEmpty(plan, priceIndex)"
            />
            <editable
              placeholder="Waluta..."
              v-model.trim="plan[Plan.PRICES][priceIndex].currency"
              :readonly="!hasPermission(Permission.CREATE_TEMPLATE)"
            />
            <p>
              <editor
                placeholder="Opcjonalna adnotacja do ceny"
                v-model.trim="plan[Plan.PRICES][priceIndex].disclaimer"
                :readonly="!hasPermission(Permission.CREATE_TEMPLATE)"
              />
            </p>
          </div>
          <protected :with="Permission.CREATE_TEMPLATE">
            <div
              class="plan-item add-feature"
              @click="newPrice(plan, planIndex)"
            >
              <icon name="fa-plus-circle" />
            </div>
          </protected>
          <p>
            <editor
              placeholder="Opcjonalny opis pakietu..."
              v-model.trim="plan[Plan.DESCRIPTION]"
              :readonly="!hasPermission(Permission.CREATE_TEMPLATE)"
            />
          </p>
          <div class="plan-items">
            <div
              class="plan-item"
              v-for="(feature, featureIndex) in plan[Plan.FEATURES]"
              :key="featureIndex"
            >
              <editable
                placeholder="Cecha/zaleta pakietu..."
                v-model.trim="plan[Plan.FEATURES][featureIndex]"
                :ref="`feature:${planIndex}:${featureIndex}`"
                @blur="removeFeatureIfEmpty(plan, featureIndex)"
                :readonly="!hasPermission(Permission.CREATE_TEMPLATE)"
              />
            </div>
            <protected :with="Permission.CREATE_TEMPLATE">
              <div
                class="plan-item add-feature"
                @click="newFeature(plan, planIndex)"
              >
                <icon name="fa-plus-circle" />
              </div>
            </protected>
          </div>
          <div class="plan-footer">
            <protected :with="Permission.CREATE_TEMPLATE">
              <div class="level">
                <div class="level-item">
                  <icon
                    name="fa-trash"
                    class="plan-control remove-plan"
                    data-tooltip="Usuń pakiet"
                    @click="removePlan(planIndex)"
                  />
                </div>
                <div class="level-item">
                  <icon
                    name="fa-arrow-left"
                    class="plan-control"
                    data-tooltip="Przesuń pakiet w lewo"
                    @click="movePlanToLeft(planIndex)"
                    v-if="planIndex > 0"
                  />
                </div>
                <div class="level-item">
                  <icon
                    name="fa-arrow-right"
                    class="plan-control"
                    @click="movePlanToRight(planIndex)"
                    data-tooltip="Przesuń pakiet w prawo"
                    v-if="planIndex < table.length - 1"
                  />
                </div>
                <div class="level-item">
                  <icon
                    class="select-plan plan-control"
                    :class="{ 'select-plan--selected': plan[Plan.SELECTED] }"
                    name="fa-star"
                    :data-tooltip="
                      plan[Plan.SELECTED]
                        ? 'Usuń wyróżnienie'
                        : 'Wyróżnij pakiet'
                    "
                    @click="toggleSelection(plan)"
                  />
                </div>
              </div>
            </protected>
          </div>
        </div>

        <protected :with="Permission.CREATE_TEMPLATE" key="add-plan">
          <div class="add-plan pricing-plan" @click="newPlan">
            <icon name="fa-plus-circle" />
          </div>
        </protected>
      </transition-group>

      <template #loading>
        <div
          class="spinner is-3by4 is-grey-lighter is-large is-thick is-centered"
        />
      </template>

      <template #error>
        <div class="grey-message">
          <icon name="fa-times-circle" />
          <p>Dane niedostępne z powodu braku połączenia z serwerem.</p>
        </div>
      </template>
    </loader>
  </div>
</template>

<script>
import { Protected } from "%mixins";
import Editor from "%admin/components/global/Editor";
import { Editable, Icon, Loader, MessageBox } from "%helpers";

// NOTE: Remember to update corresponding values order
// in setPricing and newPlan methods
const Plan = {
  ID: 0,
  NAME: 1,
  PRICES: 2,
  DESCRIPTION: 3,
  FEATURES: 4,
  SELECTED: 5
};

export default {
  name: "pricing-input",

  components: {
    Editable,
    Editor,
    Icon,
    Loader,
    MessageBox
  },

  mixins: [Protected],

  props: {
    state: String
  },

  data() {
    return {
      table: [],

      Plan
    };
  },

  methods: {
    setPricing(pricing) {
      this.table = pricing.map(plan => [
        Symbol(),
        plan.title || "",
        [...plan.prices] || [],
        plan.description || "",
        [...plan.features] || [],
        plan.selected || false
      ]);
    },

    async newPlan() {
      this.table.push([Symbol(), "", [], "", [], false]);
      await this.$nextTick();
      this.$refs[`name:${this.table.length - 1}`][0].focus();
    },

    async newFeature(plan, planIndex) {
      plan[Plan.FEATURES].push("");
      await this.$nextTick();
      this.$refs[
        `feature:${planIndex}:${plan[Plan.FEATURES].length - 1}`
      ][0].focus();
    },

    async newPrice(plan, planIndex) {
      plan[Plan.PRICES].push({
        price: "",
        currency: "",
        disclaimer: ""
      });
      await this.$nextTick();
      this.$refs[
        `price:${planIndex}:${plan[Plan.PRICES].length - 1}`
      ][0].focus();
    },

    removePriceIfEmpty(plan, index) {
      if (plan[Plan.PRICES][index].price.trim().length === 0) {
        plan[Plan.PRICES].splice(index, 1);
      }
    },

    movePlanToLeft(index) {
      if (index > 0) {
        this.swapPlans(index, index - 1);
      }
    },

    movePlanToRight(index) {
      if (index < this.table.length - 1) {
        this.swapPlans(index, index + 1);
      }
    },

    swapPlans(a, b) {
      const tmp = this.table[a];
      this.table.splice(a, 1, this.table[b]);
      this.table.splice(b, 1, tmp);
    },

    removeFeatureIfEmpty(plan, index) {
      if (plan[Plan.FEATURES][index].trim().length === 0) {
        plan[Plan.FEATURES].splice(index, 1);
      }
    },

    removePlanIfNameIsEmpty(index) {
      if (this.table[index][Plan.NAME].trim().length === 0) {
        this.table.splice(index, 1);
      }
    },

    removePlan(index) {
      this.table.splice(index, 1);
    },

    toggleSelection(plan) {
      plan.splice(Plan.SELECTED, 1, !plan[Plan.SELECTED]);
    }
  },

  watch: {
    table(value) {
      this.$emit(
        "update",
        value.map(plan => ({
          title: plan[Plan.NAME],
          prices: [...plan[Plan.PRICES]],
          description: plan[Plan.DESCRIPTION],
          features: [...plan[Plan.FEATURES]],
          selected: plan[Plan.SELECTED]
        }))
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.pricing-table {
  justify-content: flex-start;

  .pricing-plan {
    min-width: 15rem;
    max-width: 20rem;

    .plan-price-amount {
      margin-right: 0.5rem;
    }

    .add-feature {
      background: transparent;
      color: $grey-light;
      font-size: 1.2rem;
      transition: 0.1s;
      cursor: pointer;

      &:hover {
        background: lighten($grey-lighter, 5);
        color: $grey;
        transition: 0.1s;
      }
    }

    .plan-control {
      color: $grey-lighter;
      cursor: pointer;
      font-size: 1rem;

      &:hover {
        color: $grey-light;
      }
    }

    .remove-plan {
      &:hover {
        color: $danger;
      }
    }

    .select-plan {
      &:hover {
        color: $primary;
      }

      &--selected {
        color: $primary;

        &:hover {
          color: lighten($primary, 5);
        }
      }
    }

    &.add-plan {
      cursor: pointer;
      color: $grey-lighter;
      transition: 0.1s;
      min-height: 20rem;
      min-width: 15rem;
      max-width: 15rem;

      .icon {
        cursor: pointer;
        font-size: 4rem;
        margin: auto auto;
      }

      &:hover {
        border-color: $grey-light;
        color: $grey-light;
        transition: 0.1s;
      }
    }
  }
}
</style>
